/**
 * Canonical pricing / token catalog.
 *
 * Single source of truth for BOTH the public landing page and the in-app
 * industry plans (src/services/industry/planService.ts derives IndustryPlan[]
 * from PLANS), so the two can never drift.
 */
import type { MembershipTier, BusinessSize } from '@/types';

export interface Plan {
  id: string;
  name: string;
  description: string;
  /** Display price, e.g. "$29" or "Custom". */
  price: string;
  /** Display suffix, e.g. "/month" or "". */
  priceNote: string;
  /** Numeric prices used for checkout math (0 = custom / contact sales). */
  priceMonthly: number;
  priceYearly: number;
  /** Membership tier used for in-app feature gating. */
  tier: MembershipTier;
  businessSize: BusinessSize;
  /** Display token count, e.g. "500" or "20,000+". */
  tokensPerMonth: string;
  /** Numeric monthly token allowance. */
  monthlyTokens: number;
  overusePrice: string;
  /** Included feature names (also fed into the in-app feature matrix). */
  features: string[];
  automationLimit: string;
  seatLimit: number | 'unlimited';
  supportLevel: 'community' | 'email' | 'priority' | 'dedicated';
  freeTrialDays?: number;
  highlighted?: boolean;
  cta: string;
}

export const PLANS: Plan[] = [
  {
    id: 'plan_starter',
    name: 'Starter',
    description: 'Everything a small team needs to validate and launch.',
    price: '$29',
    priceNote: '/month',
    priceMonthly: 29,
    priceYearly: 290,
    tier: 'starter',
    businessSize: 'Startup',
    tokensPerMonth: '500',
    monthlyTokens: 500,
    overusePrice: '$0.08 / token',
    features: ['Basic AI matching', 'Industry trend charts', '5 active jobs'],
    automationLimit: 'Add up to 2 automation features',
    seatLimit: 5,
    supportLevel: 'email',
    cta: 'Choose Starter',
  },
  {
    id: 'plan_pro',
    name: 'Pro',
    description: 'For growing SMEs that need richer analytics and automation.',
    price: '$79',
    priceNote: '/month',
    priceMonthly: 79,
    priceYearly: 790,
    tier: 'pro',
    businessSize: 'SME',
    tokensPerMonth: '2,000',
    monthlyTokens: 2000,
    overusePrice: '$0.06 / token',
    features: ['Full AI industry assessment', 'Candidate ranking', '25 active jobs', 'Custom reports'],
    automationLimit: 'Add up to 5 automation features',
    seatLimit: 25,
    supportLevel: 'priority',
    highlighted: true,
    cta: 'Choose Pro',
  },
  {
    id: 'plan_business',
    name: 'Business',
    description: 'Scale across teams with unlimited jobs, API access and full automation.',
    price: '$199',
    priceNote: '/month',
    priceMonthly: 199,
    priceYearly: 1990,
    tier: 'enterprise',
    businessSize: 'Enterprise',
    tokensPerMonth: '6,000',
    monthlyTokens: 6000,
    overusePrice: '$0.05 / token',
    features: ['Unlimited jobs', 'Team collaboration', 'API access', 'Competitive analysis', 'Onboarding automation'],
    automationLimit: 'All automation features included',
    seatLimit: 'unlimited',
    supportLevel: 'priority',
    cta: 'Choose Business',
  },
  {
    id: 'plan_enterprise',
    name: 'Enterprise',
    description: 'Dedicated AI agent, custom automation workflows, SLA and SSO.',
    price: 'Custom',
    priceNote: '',
    priceMonthly: 0,
    priceYearly: 0,
    tier: 'enterprise',
    businessSize: 'Enterprise',
    tokensPerMonth: '20,000+',
    monthlyTokens: 20000,
    overusePrice: 'Negotiable',
    features: ['Dedicated AI agent', 'Custom automation workflows', 'SLA', 'SSO'],
    automationLimit: 'All automation features included',
    seatLimit: 'unlimited',
    supportLevel: 'dedicated',
    cta: 'Contact Sales',
  },
];

/** Free trial: a one-time grant of tokens with no time limit and no payment. */
export const FREE_TRIAL_TOKENS = 50;

/** Token cost of each metered AI operation (mirrors TOKEN_USAGE below). */
export const TOKEN_COSTS = {
  assessment: 25,
  aiMatch: 15,
  marketAnalysis: 30,
  compareIndustries: 20,
  jobDescription: 10,
} as const;

export type TokenAction = keyof typeof TOKEN_COSTS;

export const PLAN_PERKS: string[] = [
  'Unused tokens roll over for up to 3 months.',
  'Free trial users can upgrade and keep remaining free tokens.',
  'Cancel anytime – no hidden fees.',
];

export interface TokenUsage {
  action: string;
  cost: number;
}

export const TOKEN_USAGE: TokenUsage[] = [
  { action: 'Run industry assessment (10 questions)', cost: 25 },
  { action: 'AI match business to best industry (one company profile)', cost: 15 },
  { action: 'Generate market analysis report (5 pages)', cost: 30 },
  { action: 'Compare up to 3 industries side-by-side', cost: 20 },
  { action: 'AI job description writer (1 posting)', cost: 10 },
  { action: 'Rank candidates for a job (per 100 applicants)', cost: 40 },
  { action: 'Automate interview scheduling (per invitation)', cost: 5 },
  { action: 'Generate recruitment funnel chart', cost: 8 },
  { action: 'Export analytics PDF (per report)', cost: 12 },
];

export interface AutomationAddon {
  feature: string;
  monthlyFee: string;
  tokenCost: string;
}

export const AUTOMATION_ADDONS: AutomationAddon[] = [
  { feature: 'Auto-post jobs to 5+ boards', monthlyFee: '$19', tokenCost: '5 tokens / posting' },
  { feature: 'Daily industry trend alerts (email/Slack)', monthlyFee: '$9', tokenCost: '2 tokens / alert' },
  { feature: 'AI candidate screening & reply', monthlyFee: '$29', tokenCost: '15 tokens / 50 candidates' },
  { feature: 'Scheduled market reports (weekly)', monthlyFee: '$15', tokenCost: '20 tokens / report' },
  { feature: 'Auto-update competitive landscape', monthlyFee: '$25', tokenCost: '10 tokens / update' },
];

export const AUTOMATION_LIMITS: string[] = [
  'Starter can add up to 2 automation features.',
  'Pro can add up to 5 automation features.',
  'Business & Enterprise include all automation features for no extra monthly fee (tokens still apply per run).',
];

export interface TokenPack {
  tokens: string;
  price: string;
  bestFor: string;
}

export const TOKEN_PACKS: TokenPack[] = [
  { tokens: '500', price: '$35', bestFor: 'Additional tokens for Starter' },
  { tokens: '2,000', price: '$110', bestFor: 'Supplement Pro plan' },
  { tokens: '10,000', price: '$450', bestFor: 'Heavy analysis & recruitment months' },
];

export interface Faq {
  q: string;
  a: string;
}

export const FAQS: Faq[] = [
  { q: 'What is a token?', a: '1 token ≈ 1 AI operation — for example one industry match, one report page, or one candidate rank. Every AI action draws from your token balance.' },
  { q: 'Do free trial tokens expire?', a: 'No. The 50 free trial tokens are a one-time grant with no time limit. Use them whenever you like, and keep any remaining tokens when you upgrade.' },
  { q: 'What happens if I run out of tokens?', a: 'You can buy a one-time Token Pack anytime, or rely on over-use pricing on paid plans. Your plan tokens also renew every month.' },
  { q: 'Do unused tokens roll over?', a: 'On paid plans, unused tokens roll over for up to 3 months so you never lose what you’ve paid for.' },
  { q: 'How does automation pricing work?', a: 'Automation add-ons have a small monthly fee plus a token cost per run. Business & Enterprise plans include all add-ons with no extra monthly fee (tokens still apply per run).' },
  { q: 'Can I cancel anytime?', a: 'Yes — cancel anytime with no hidden fees. Your tokens remain available until the end of the billing period.' },
];
